/**
 * FacilityDrilldown — the "who exactly is in the building" modal.
 *
 * Extracted verbatim from FacilityStatus.tsx (Wave 1, 2026-05-11) so the
 * Lane-D facility hero (spec D1, 2026-07-09 wave) can reuse it: tapping a
 * per-service occupancy meter opens the same in-building roster —
 * including mid-stay boarders — from GET /api/admin/dashboard/
 * facility-details. Behavior, class names and testids are unchanged
 * (the spec's "preserve the existing drilldown modal behavior").
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
// Ted #28 (#1050) — the on-site roster ("checked-in dogs") carries the
// stay's flags like every other surface; the one renderer.
import { DogFlagChips } from '../dog-first/index.js';
import {
  fetchFacilityDetails,
  formatTime,
  type DashboardServiceKey,
  type FacilityDetailDog,
} from './dashboard-api.js';

const SERVICE_LABEL: Record<DashboardServiceKey, string> = {
  daycare: 'Daycare',
  boarding: 'Boarding',
  grooming: 'Grooming',
};

interface FacilityDrilldownProps {
  apiUrl: string;
  token: string;
  date: string;
  service: DashboardServiceKey;
  onClose: () => void;
}

export function FacilityDrilldown({
  apiUrl,
  token,
  date,
  service,
  onClose,
}: FacilityDrilldownProps): JSX.Element {
  const [dogs, setDogs] = useState<FacilityDetailDog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    void fetchFacilityDetails(apiUrl, token, date, service)
      .then((res) => {
        if (cancelled) return;
        setDogs(res.dogs);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load drilldown';
        setError(message);
        setDogs([]);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, token, date, service]);

  useEffect(() => {
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const timeoutId = window.setTimeout(() => closeRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(timeoutId);
      restoreFocusRef.current?.focus();
    };
  }, []);

  const handleDialogKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  return (
    <div
      className="ferro-drilldown"
      data-testid="facility-drilldown"
    >
      <button
        type="button"
        aria-label="Close drilldown"
        data-testid="facility-drilldown-backdrop"
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          padding: 0,
          border: 0,
          background: 'transparent',
          cursor: 'default',
        }}
      />
      <div
        className="ferro-drilldown__inner"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${SERVICE_LABEL[service]} drilldown`}
        onKeyDown={handleDialogKeyDown}
        style={{ position: 'relative' }}
      >
        <header className="ferro-drilldown__header">
          <h3 className="ferro-drilldown__title">
            {SERVICE_LABEL[service]}
            {!isLoading && (
              <span className="ferro-drilldown__pill">
                {dogs.length} dog{dogs.length === 1 ? '' : 's'}
              </span>
            )}
          </h3>
          <button
            type="button"
            ref={closeRef}
            onClick={onClose}
            className="ferro-arrivals__close"
            aria-label="Close drilldown"
          >
            <svg
              className="ferro-arrivals__close-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </header>

        {error && (
          <div className="ferro-dashboard-widget__error" role="alert">
            {error}
          </div>
        )}

        {isLoading && (
          <div
            className="ferro-arrivals__skeleton"
            data-testid="facility-drilldown-loading"
          >
            <div className="ferro-arrivals__skeleton-row" />
            <div className="ferro-arrivals__skeleton-row" />
            <div className="ferro-arrivals__skeleton-row" />
          </div>
        )}

        {!isLoading && dogs.length === 0 && !error && (
          <p className="ferro-drilldown__empty">
            No {SERVICE_LABEL[service].toLowerCase()} dogs for this date.
          </p>
        )}

        {!isLoading && dogs.length > 0 && (
          <ul className="ferro-drilldown__list">
            {dogs.map((dog) => (
              <li key={dog.dogId}>
                {/* Johnny, 09-23: "I can't click on the dogs." Every other
                    roster of dogs (day list, dog cards, search) opens the dog;
                    this one was a dead end. The whole row is the target —
                    leaving the dashboard unmounts the modal, so nothing to close. */}
                <Link
                  to={`/dogs/${encodeURIComponent(dog.dogId)}`}
                  className="ferro-drilldown__item ferro-drilldown__item--link"
                  data-testid={`facility-drilldown-dog-${dog.dogId}`}
                >
                  <div className="ferro-drilldown__avatar">
                    {dog.photoUrl ? (
                      <img src={dog.photoUrl} alt={dog.dogName} />
                    ) : (
                      <span className="ferro-drilldown__initials">
                        {dog.dogName.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="ferro-drilldown__body">
                    <p className="ferro-drilldown__name">
                      {dog.dogName}{' '}
                      <DogFlagChips flags={dog.flags} testIdPrefix="drilldown-" />
                    </p>
                    <p className="ferro-drilldown__meta">
                      {dog.ownerName}
                      {/* Ted #28 Phase 2 — the owner's flags under the owner. */}
                      <DogFlagChips flags={dog.flags} scope="account" testIdPrefix="drilldown-owner-" />
                      {dog.breed ? ` · ${dog.breed}` : ''}
                      {dog.sizeCategory ? ` · ${dog.sizeCategory}` : ''}
                    </p>
                  </div>
                  {dog.checkInTime && (
                    <span className="ferro-drilldown__time">
                      In {formatTime(dog.checkInTime)}
                    </span>
                  )}
                  <svg
                    className="ferro-drilldown__chevron"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
